import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { submitContact } from "@/lib/api";
import Turnstile from "@/components/Turnstile";
import { Eyebrow } from "./SectionHeading";
import { contactInterests, onContactInterest } from "./contactInterest";

const emptyForm = { name: "", email: "", company: "", interest: "", message: "" };

type FormState = typeof emptyForm;
type FieldErrors = Partial<Record<"name" | "email" | "message" | "captcha" | "submit", string>>;

const fieldClass =
  "box-border w-full rounded-xl border bg-[var(--input-bg)] px-3.5 text-base font-normal text-foreground outline-none transition-[border-color,box-shadow] placeholder:text-muted-foreground/85 focus:border-accent focus:shadow-[0_0_0_4px_var(--focus-ring)]";

const borderFor = (error?: string) => (error ? "border-destructive" : "border-border");

const ErrorText = ({ children }: { children?: string }) =>
  children ? <span className="text-[13px] font-normal text-destructive-text">{children}</span> : null;

const validate = (form: FormState): FieldErrors => {
  const errors: FieldErrors = {};
  if (form.name.trim().length < 2) errors.name = "Please add your name.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errors.email = "Please enter a valid email.";
  if (form.message.trim().length < 10) errors.message = "Add a short message.";
  return errors;
};

const ContactSection = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [sending, setSending] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  useEffect(() => onContactInterest((interest) => setForm((f) => ({ ...f, interest }))), []);

  const onField = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((errs) => ({ ...errs, [name]: undefined, submit: undefined }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const nextErrors = validate(form);
    if (!captchaToken) nextErrors.captcha = "Please confirm you're human before sending.";
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setSending(true);
    setErrors({});
    try {
      await submitContact({
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || undefined,
        need: form.interest || undefined,
        message: form.message.trim(),
        captchaToken: captchaToken!,
      });
      navigate(`/verify-email?email=${encodeURIComponent(form.email.trim())}`);
    } catch (error) {
      console.error("Error submitting form:", error);
      // Token is single-use; force a fresh challenge on retry
      setCaptchaToken(null);
      setErrors({ submit: "We couldn't send your message. Please try again." });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative scroll-mt-[68px] overflow-hidden border-t border-border bg-grad-hero">
      <div
        className="pointer-events-none absolute -bottom-[400px] -left-[300px] h-[800px] w-[800px] rounded-full bg-[radial-gradient(circle,var(--orb),transparent_65%)]"
        aria-hidden="true"
      />
      <div className="relative mx-auto flex max-w-[1200px] flex-wrap items-start gap-x-[72px] gap-y-12 px-[clamp(16px,5vw,48px)] py-[clamp(80px,10vw,140px)]">
        <div className="flex-[1_1_320px]">
          <Eyebrow index="05" label="Contact" />
          <h2 className="text-balance mt-[18px] text-[clamp(34px,4.6vw,60px)] font-semibold leading-[1.04] tracking-[-0.04em]">
            Building something with AI? Let's talk.
          </h2>
          <p className="mt-6 text-lg leading-[1.6] text-muted-foreground">Tell us what you're working on.</p>
          <a href="mailto:hello@sksenq.com" className="mt-3 inline-block font-mono text-[15px] text-accent-text hover:text-foreground">
            hello@sksenq.com
          </a>
        </div>

        <div className="min-w-0 flex-[1.2_1_420px] rounded-[20px] border border-border bg-card p-[clamp(20px,4vw,40px)] shadow-glow">
          <form
            onSubmit={handleSubmit}
            noValidate
            className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,220px),1fr))] gap-5"
          >
            <label className="flex flex-col gap-2 text-sm font-medium">
              Name
              <input
                name="name"
                value={form.name}
                onChange={onField}
                autoComplete="name"
                aria-invalid={!!errors.name}
                className={cn(fieldClass, "h-[50px]", borderFor(errors.name))}
              />
              <ErrorText>{errors.name}</ErrorText>
            </label>
            <label className="flex flex-col gap-2 text-sm font-medium">
              Email
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={onField}
                autoComplete="email"
                aria-invalid={!!errors.email}
                className={cn(fieldClass, "h-[50px]", borderFor(errors.email))}
              />
              <ErrorText>{errors.email}</ErrorText>
            </label>
            <label className="flex flex-col gap-2 text-sm font-medium">
              Company
              <input
                name="company"
                value={form.company}
                onChange={onField}
                autoComplete="organization"
                className={cn(fieldClass, "h-[50px] border-border")}
              />
            </label>
            <label className="flex flex-col gap-2 text-sm font-medium">
              Interested in
              <span className="relative block">
                <select
                  name="interest"
                  value={form.interest}
                  onChange={onField}
                  className={cn(fieldClass, "h-[50px] cursor-pointer appearance-none border-border pr-10")}
                >
                  <option value="">Choose one</option>
                  {contactInterests.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  className="pointer-events-none absolute right-4 top-[18px] text-muted-foreground"
                  aria-hidden="true"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </span>
            </label>
            <label className="col-span-full flex flex-col gap-2 text-sm font-medium">
              Message
              <textarea
                name="message"
                value={form.message}
                onChange={onField}
                rows={5}
                placeholder="What are you working on?"
                aria-invalid={!!errors.message}
                className={cn(fieldClass, "resize-y py-3 leading-[1.5]", borderFor(errors.message))}
              />
              <ErrorText>{errors.message}</ErrorText>
            </label>

            <div className="col-span-full flex flex-col gap-2">
              <Turnstile
                onVerify={(token) => {
                  setCaptchaToken(token);
                  setErrors((errs) => ({ ...errs, captcha: undefined }));
                }}
                onExpire={() => setCaptchaToken(null)}
              />
              <ErrorText>{errors.captcha}</ErrorText>
            </div>

            <div className="col-span-full mt-1 flex flex-wrap items-center justify-between gap-4">
              <span className="text-sm text-muted-foreground">We reply within one business day.</span>
              <button
                type="submit"
                disabled={sending}
                className="min-h-[52px] cursor-pointer rounded-xl border-none bg-btn px-8 text-base font-semibold text-[color:var(--btn-fg)] shadow-primary transition-[transform,box-shadow] hover:-translate-y-px hover:shadow-glow disabled:cursor-default disabled:opacity-70 disabled:hover:translate-y-0"
              >
                {sending ? "Sending…" : "Send"}
              </button>
            </div>
            {errors.submit && (
              <p className="col-span-full text-sm text-destructive-text" role="alert">
                {errors.submit}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
